/**
 * Post Create API
 *
 * API and upload helpers used by the post create flow.
 */

import { BoardId } from "../../constants/board-id";
import { VideoUtils } from "../../util/media/video/video-utils";
import { ApiBase } from "../../core/api-base";
import { z } from "zod";
import { CreateBoardPost201Response } from "../../api/generated/posts.zod";
import { PostCreateUtils } from "./post-create-utils";

interface PostCreateApiConfig {
    maxImages: number;
    maxVideos: number;
    imageQuality: number;
    maxImageSize: number;
    maxVideoSize: number;
    allowedImageTypes: string[];
    allowedVideoTypes: string[];
}

export type PostCreateJsonPrimitive = string | number | boolean | null;
export type PostCreateJsonValue = PostCreateJsonPrimitive | PostCreateJsonObject | PostCreateJsonValue[];
export type PostCreateJsonObject = { [key: string]: PostCreateJsonValue };

type PostCreateSuccessResponse = z.infer<typeof CreateBoardPost201Response>;

interface PostCreateRedirectResponse {
    success?: boolean | 0 | 1;
    message?: string;
    redirect_to?: string;
}

export type PostCreateSubmitData = PostCreateSuccessResponse;

interface PostCreateSubmitResult {
    success: boolean;
    data?: PostCreateSubmitData;
    error?: PostCreateSubmitError;
}

export type PostCreateSubmitError = Error | string;

export interface ImagePreviewData {
    id: number;
    file: File;
    name: string;
    size: number;
    type: string;
    previewUrl: string | null;
}

export type VideoPreviewData = Awaited<ReturnType<typeof VideoUtils.generateThumbnail>>;

function isPostCreateRedirectResponse(value: unknown): value is PostCreateRedirectResponse {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        return false;
    }

    const payload = value as Record<string, unknown>;

    return (
        (payload.redirect_to === undefined || typeof payload.redirect_to === "string") &&
        (payload.message === undefined || typeof payload.message === "string") &&
        (payload.success === undefined ||
            payload.success === 0 ||
            payload.success === 1 ||
            typeof payload.success === "boolean")
    );
}

class PostCreateApiService extends ApiBase {
    public config: PostCreateApiConfig;

    public constructor() {
        super();
        this.config = {
            maxImages: 10,
            maxVideos: 5,
            imageQuality: 0.9,
            maxImageSize: 5 * 1024 * 1024,
            maxVideoSize: 50 * 1024 * 1024,
            allowedImageTypes: ["image/jpeg", "image/png", "image/gif", "image/webp"],
            allowedVideoTypes: ["video/mp4", "video/webm", "video/ogg", "video/quicktime"],
        };
    }

    public async init(): Promise<void> {
        return Promise.resolve();
    }

    public async submitPost(formData: FormData): Promise<PostCreateSubmitResult> {
        try {
            const boardIdFromForm = (() => {
                try {
                    const value = formData?.get?.("board_id");
                    return typeof value === "string" && value.trim() ? value.trim() : null;
                } catch (_error) {
                    return null;
                }
            })();

            const boardId = boardIdFromForm || PostCreateUtils.getBoardIdFromPath() || BoardId.REVIEW;
            const endpoint = `/api/v1/posts/${boardId}`;
            const rawData = await this.formRequest(endpoint, formData, "POST");
            const successResponse = CreateBoardPost201Response.safeParse(rawData);

            if (successResponse.success) {
                return {
                    success: true,
                    data: successResponse.data,
                };
            }

            if (isPostCreateRedirectResponse(rawData)) {
                if (rawData.redirect_to) {
                    const error = new Error(rawData.message || "authentication_required") as Error & {
                        redirect_to?: string;
                    };
                    error.redirect_to = rawData.redirect_to;
                    throw error;
                }

                throw new Error(rawData.message || "post_create_failed");
            }

            throw new Error("post_create_failed");
        } catch (error) {
            console.error("Submit post error:", error);
            return {
                success: false,
                error: error instanceof Error ? error : typeof error === "string" ? error : "post_create_failed",
            };
        }
    }

    public async processImageFile(file: File): Promise<Blob | null> {
        return await new Promise((resolve, reject) => {
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            const img = new Image();

            img.onload = () => {
                if (!ctx) {
                    resolve(null);
                    return;
                }

                const maxWidth = 1920;
                const maxHeight = 1080;
                let { width, height } = img;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => resolve(blob), "image/jpeg", this.config.imageQuality);
            };

            img.onerror = () => reject(new Error("Failed to load image for compression"));
            img.src = URL.createObjectURL(file);
        });
    }

    public generateImagePreview(file: File): Promise<ImagePreviewData> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                const previewUrl = typeof event.target?.result === "string" ? event.target.result : null;
                resolve({
                    id: Date.now() + Math.random(),
                    file,
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    previewUrl,
                });
            };

            reader.onerror = () => reject(reader.error || new Error("Failed to read image file"));
            reader.readAsDataURL(file);
        });
    }

    public async generateVideoPreview(file: File): Promise<VideoPreviewData> {
        return await VideoUtils.generateThumbnail(file);
    }

    public getConfig(): PostCreateApiConfig {
        return { ...this.config };
    }
}

export { PostCreateApiService };
